/** Replace only text nodes so order values can never become document markup. */
export function fillRentalContract(content: Record<string, unknown>, values: Partial<Record<string, string>>) {
  const unresolved = new Set<string>()
  const resolve = (key: string) => {
    if (Object.hasOwn(values, key) && values[key] !== undefined && values[key] !== '') {
      return values[key]
    }
    unresolved.add(key)
    return '\u00a0'
  }
  const isText = (value: unknown): value is Record<string, unknown> & { text: string } =>
    Boolean(
      value &&
      typeof value === 'object' &&
      'type' in value &&
      value.type === 'text' &&
      'text' in value &&
      typeof value.text === 'string'
    )
  // A placeholder can span differently formatted text nodes in Lexical.
  const fillTextRun = (nodes: Array<Record<string, unknown> & { text: string }>) => {
    const text = nodes.map((node) => node.text).join('')
    const matches = [...text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((match) => ({
      start: match.index,
      end: match.index + match[0].length,
      value: resolve(match[1])
    }))
    let offset = 0
    return nodes.map((node) => {
      const start = offset
      const end = start + node.text.length
      offset = end
      let cursor = start
      let result = ''
      for (const match of matches) {
        if (match.end <= start || match.start >= end) {
          continue
        }
        result += text.slice(cursor, Math.max(cursor, match.start))
        if (match.start >= start) {
          result += match.value
        }
        cursor = Math.min(end, match.end)
      }
      result += text.slice(cursor, end)
      return { ...node, text: result }
    })
  }
  const visit = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      const result: unknown[] = []
      for (let index = 0; index < value.length;) {
        if (!isText(value[index])) {
          result.push(visit(value[index++]))
          continue
        }
        const run: Array<Record<string, unknown> & { text: string }> = []
        while (index < value.length && isText(value[index])) {
          run.push(value[index++])
        }
        result.push(...fillTextRun(run))
      }
      return result
    }
    if (!value || typeof value !== 'object') {
      return value
    }
    const node = { ...value } as Record<string, unknown>
    if (node.type === 'contract-variable' && typeof node.variableKey === 'string') {
      node.text = resolve(node.variableKey)
      node.type = 'text'
      node.mode = 'normal'
      delete node.variableKey
    } else if (node.type === 'text' && typeof node.text === 'string') {
      node.text = node.text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key: string) => resolve(key))
    }
    if (node.children) {
      node.children = visit(node.children)
    }
    if (node.root) {
      node.root = visit(node.root)
    }
    return node
  }
  return { content: visit(content) as Record<string, unknown>, unresolved: [...unresolved] }
}
